import { gerReferrer, getSourceUrl } from "../lib/utils";
import { Beacon } from "./beacon";

// https://eips.ethereum.org/EIPS/eip-1193
export interface EIP1193Provider {
  on(eventName: string | symbol, listener: (...args: unknown[]) => void): this;
  removeListener(
    eventName: string | symbol,
    listener: (...args: unknown[]) => void
  ): this;
  selectedAddress?: string;
}

declare global {
  interface Window {
    ethereum?: EIP1193Provider;
    web3?: {
      currentProvider?: EIP1193Provider;
    };
  }
}

export function createWalletConnectsPlugin(beacon: Beacon) {
  function attribute(address: string) {
    const sourceUrl = getSourceUrl();
    const referrer = gerReferrer();

    return beacon.sendBeacon({
      type: "WALLET_CONNECTED",
      data: { address, sourceUrl, referrer },
    });
  }

  function enableAutoWalletConnects() {
    // we are keeping track of connected address. if already connected, don't emit the wallet connect twice.
    let connectedAddress: string;

    const provider = window?.ethereum || window.web3?.currentProvider;
    const listener = (...args: unknown[]) => {
      const accounts = args[0] as string[];

      // accountsChanged also gets called on disconnect so we don't want to attirbute in that scenario
      if (accounts.length > 0 && accounts[0] !== connectedAddress) {
        attribute(accounts[0]);
        connectedAddress = accounts[0];
      } else {
        // when accounts.length == 0, it means this is a disconnect event
        connectedAddress = undefined;
      }
    };

    // typically gets triggered when browser extension wallets like metamask connects or changes wallets
    // does not necessarily get executed when people click "connect" in browser if metamask is already connected
    provider?.on("accountsChanged", listener);

    // on browser load for metamask, check if there is a selected address already connected in browser & send it as wallet connect event
    addEventListener("load", (event) => {
      const selectedAddress =
        window?.ethereum?.selectedAddress ||
        window.web3?.currentProvider?.selectedAddress;
      if (selectedAddress && connectedAddress !== selectedAddress) {
        connectedAddress = selectedAddress;
        attribute(selectedAddress);
      }
    });

    return function () {
      if (provider) {
        provider.removeListener("accountsChanged", listener);
      }
    };
  }

  return {
    attribute,
    enableAutoWalletConnects,
  };
}
